/**
 * Site footer, the same as the corporate site's (new_wp / providecars.co.jp,
 * English chrome): Royal Navy background, company column with the JUMVEA
 * badge, three link columns and a bottom bar (copyright + social icons +
 * legal). Every link points at the corporate site (absolute URLs) — the
 * portal itself has none of these pages.
 */
import { FacebookIcon, InstagramIcon, YoutubeIcon } from './SocialIcons';
import { AUCTION_SIGNUP_HREF, SITE_HREF } from './chromeNav';

interface FooterLink {
  label: string;
  href: string;
}

const site = (path: string): string => `${SITE_HREF}${path}`;

const JAPAN_AUCTIONS: FooterLink[] = [
  { label: 'USS Auction Guide', href: site('/about-auction/about-uss-auction') },
  { label: 'TAA Auction Guide', href: site('/about-auction/about-taa-auction') },
  { label: 'CAA Auction Guide', href: site('/about-auction/caa-auction') },
  { label: 'Auction Days & Venues', href: site('/about-auction/japanese-car-auction-days') },
  {
    label: 'Auction Grading System',
    href: site('/about-auction/japanese-auction-houses-use-a-grading-system'),
  },
];

const IMPORT_PROCESS: FooterLink[] = [
  {
    label: 'Import Process from Japan',
    href: site('/about-auction/detailed-provide-cars-import-process'),
  },
  { label: 'Commission & Fees', href: site('/bank-info/service-and-commission') },
  { label: 'FOB vs CIF Guide', href: site('/bank-info/fob-cif') },
  { label: 'Payment & Bank Info', href: site('/bank-info') },
  { label: 'Import to Ireland', href: site('/ie') },
  { label: 'Import to UK', href: site('/uk') },
];

const QUICK_LINKS: FooterLink[] = [
  { label: 'Blog & Guides', href: site('/blog') },
  { label: 'Contact Us', href: site('/contact-us') },
  { label: 'FAQ', href: site('/qa') },
  { label: 'About Provide Cars', href: site('/about-provide-cars') },
  { label: 'JUMVEA Membership', href: site('/about-provide-cars/jumvea') },
  { label: 'Sign Up', href: AUCTION_SIGNUP_HREF },
];

const LEGAL_LINKS: FooterLink[] = [
  { label: 'Terms of Trade', href: site('/bank-info/terms-of-trade') },
  { label: 'Privacy Policy', href: site('/bank-info/privacy-policy') },
  { label: 'Anti-Social Forces', href: site('/bank-info/anti-social') },
];

const SOCIAL_LINKS = [
  { label: 'YouTube', href: 'https://www.youtube.com/@JapanCarAuctions', Icon: YoutubeIcon },
  { label: 'Facebook', href: 'https://www.facebook.com/providecars/', Icon: FacebookIcon },
  { label: 'Instagram', href: 'https://www.instagram.com/providecarsltd/', Icon: InstagramIcon },
] as const;

const LINK = 'text-sm text-brand-300 hover:text-white transition-colors duration-150';

function FooterNavLink({ link }: { link: FooterLink }): JSX.Element {
  return (
    <a href={link.href} target="_blank" rel="noopener noreferrer" className={LINK}>
      {link.label}
    </a>
  );
}

function NavColumn({ title, items }: { title: string; items: FooterLink[] }): JSX.Element {
  return (
    <nav aria-label={title}>
      <h4 className="text-sm font-semibold text-white mb-4">{title}</h4>
      <ul className="space-y-2.5">
        {items.map((link) => (
          <li key={link.href}>
            <FooterNavLink link={link} />
          </li>
        ))}
      </ul>
    </nav>
  );
}

const ICON_PIN =
  'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z';
const ICON_PIN_DOT = 'M15 11a3 3 0 11-6 0 3 3 0 016 0z';
const ICON_MAIL =
  'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z';
const ICON_PHONE =
  'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z';

function LineIcon({ paths }: { paths: string[] }): JSX.Element {
  return (
    <svg
      className="w-4 h-4 mt-0.5 shrink-0"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      {paths.map((d) => (
        <path key={d} strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
      ))}
    </svg>
  );
}

export function Footer(): JSX.Element {
  return (
    <footer className="bg-brand-900 text-brand-200 mt-auto" role="contentinfo">
      <div className="max-w-[1760px] mx-auto px-4 sm:px-6 xl:px-12 2xl:px-16 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company info */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Provide Cars</h3>
            <p className="text-sm text-brand-300 leading-relaxed mb-4">
              Your trusted partner for purchasing quality used Japanese vehicles directly from
              auction. JUMVEA certified member since 1997.
            </p>
            <address className="text-sm text-brand-300 not-italic leading-relaxed">
              <span className="flex items-start gap-2 mb-2">
                <LineIcon paths={[ICON_PIN, ICON_PIN_DOT]} />
                30-20 Megamiyama Cho, Koyoen, Nishinomiya, Japan
              </span>
              <span className="flex items-start gap-2 mb-2">
                <LineIcon paths={[ICON_MAIL]} />
                info@providecars.com
              </span>
              <span className="flex items-start gap-2">
                <LineIcon paths={[ICON_PHONE]} />
                +81 798 75 1451
              </span>
            </address>

            <a
              href="https://www.jumvea.or.jp/members/PROVIDE-294"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-3 group"
            >
              <img
                src="/images/certifications/jumvea-logo.webp"
                alt=""
                width={80}
                height={55}
                className="w-20 shrink-0 transition-opacity duration-150 group-hover:opacity-90"
                style={{ height: '55px' }}
              />
              <span className="flex flex-col leading-tight">
                <span className="text-xs font-semibold text-white">JUMVEA Certified</span>
                <span className="text-[11px] text-brand-400">
                  Japan Used Motor Vehicle Exporters Association
                </span>
              </span>
              <span className="sr-only"> (opens in new tab)</span>
            </a>
          </div>
          <NavColumn title="Japan Car Auctions" items={JAPAN_AUCTIONS} />
          <NavColumn title="Import Process" items={IMPORT_PROCESS} />
          <NavColumn title="Quick Links" items={QUICK_LINKS} />
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-brand-800">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:gap-6 lg:items-center">
              <p className="text-sm text-brand-400 order-2 sm:order-1">
                © {new Date().getFullYear()} PROVIDE CARS CO., LTD All rights reserved.
              </p>
              <ul
                aria-label="Provide Cars on social media"
                className="flex items-center gap-2 order-1 sm:order-2"
              >
                {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                  <li key={label}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${label} (opens in new tab)`}
                      className="inline-flex items-center justify-center w-10 h-10 rounded-full text-brand-300 hover:text-white hover:bg-white/10 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-brand-900"
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 lg:justify-end">
              {LEGAL_LINKS.map((link) => (
                <FooterNavLink key={link.href} link={link} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
